import React from 'react'
import { Box, Typography, Button, Card, CardContent, Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { 
  Home as HomeIcon, 
  Search as SearchIcon, 
  ArrowBack as ArrowBackIcon,
  ErrorOutline as ErrorIcon 
} from '@mui/icons-material'

const NotFound: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      p: 3,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card sx={{ 
        maxWidth: 600, 
        width: '100%',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon 
            sx={{ 
              fontSize: 120, 
              color: 'error.main', 
              mb: 2,
              opacity: 0.8
            }} 
          />
          
          <Typography variant="h1" component="h1" gutterBottom sx={{ 
            fontWeight: 'bold',
            fontSize: { xs: '3rem', md: '4rem' },
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}>
            404
          </Typography>
          
          <Typography variant="h4" component="h2" gutterBottom sx={{ 
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 2
          }}>
            {t('notFound.title', 'Seite nicht gefunden')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            {t('notFound.message', 'Die Seite, die Sie suchen, existiert nicht oder wurde verschoben. Überprüfen Sie die URL oder kehren Sie zur Startseite zurück.')}
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              {t('notFound.suggestion', 'Möglicherweise haben Sie sich in der URL vertippt oder die Seite wurde umbenannt.')}
            </Typography>
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              component={RouterLink}
              to="/"
              sx={{ 
                minWidth: 160,
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                }
              }}
            >
              {t('notFound.backToHome', 'Zur Startseite')}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{ minWidth: 160 }}
            >
              {t('notFound.goBack', 'Zurück')}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="text"
              startIcon={<SearchIcon />}
              component={RouterLink}
              to="/poems"
              sx={{ minWidth: 140 }}
            >
              {t('notFound.browsePoems', 'Gedichte durchsuchen')}
            </Button>
            
            <Button
              variant="text"
              component={RouterLink}
              to="/contact"
              sx={{ minWidth: 140 }}
            >
              {t('notFound.contact', 'Kontakt')}
            </Button>
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            {t('notFound.helpText', 'Benötigen Sie Hilfe? Kontaktieren Sie uns über die Kontaktseite.')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}

export default NotFound
